import { stableResultRows } from './searchModel';
export { stableResultRows } from './searchModel';
import { useEffect, useRef, useState } from 'react';
import { apiErrorCode } from '@frontend/features/travel-services/queries';
import type { HotelResultPage } from '@bandido/shared/types/hotels/hotel-search-results';
/** Never combine pages from different publication snapshots. No automatic cursor retry. */
export function appendHotelResultPage<T>(
  previous: HotelResultPage<T> | null,
  next: HotelResultPage<T>,
): HotelResultPage<T> {
  if (!previous) return next;
  if (
    previous.meta.results_revision !== next.meta.results_revision ||
    previous.meta.search_id !== next.meta.search_id ||
    previous.meta.count_scope !== next.meta.count_scope ||
    JSON.stringify(previous.meta.hotel_query) !== JSON.stringify(next.meta.hotel_query) ||
    JSON.stringify(previous.meta.applied_filters) !== JSON.stringify(next.meta.applied_filters)
  )
    throw new Error('HOTEL_RESULTS_CHANGED');
  return { ...next, data: [...previous.data, ...next.data] };
}
/** Rebuild one coherent view, retaining items pushed into later pages by new results. */
export async function readHotelResultWindow<T>(
  read: (signal: AbortSignal, cursor?: string) => Promise<HotelResultPage<T>>,
  signal: AbortSignal,
  pages: number,
  visible: string[] = [],
  identity?: (item: T) => string,
  onPagesRead?: (count: number) => void,
): Promise<HotelResultPage<T>> {
  let result = await read(signal);
  const cursors = new Set<string>();
  let loadedPages = 1;
  for (
    let n = 1;
    result.meta.next_cursor &&
    (n < pages || (identity && visible.some((id) => !result.data.some((item) => identity(item) === id))));
    n++
  ) {
    if (signal.aborted) throw new Error('Hotel result request aborted');
    const cursor = result.meta.next_cursor;
    if (cursors.has(cursor)) throw new Error('Repeated hotel result cursor');
    cursors.add(cursor);
    result = appendHotelResultPage(result, await read(signal, cursor));
    loadedPages++;
  }
  if (!signal.aborted) onPagesRead?.(loadedPages);
  return result;
}
export function hotelRevisionRetry(failures: number) {
  return failures <= 3 ? { error: 'changed' as const, again: true } : { error: 'exhausted' as const, again: false };
}
export function useHotelResultCollection<T>(
  key: string,
  read: (signal: AbortSignal, cursor?: string) => Promise<HotelResultPage<T>>,
  polling = false,
  identity?: (item: T) => string,
) {
  const [stored, setStored] = useState<{ key: string; page: HotelResultPage<T> | null }>({ key, page: null });
  const page = stored.key === key ? stored.page : null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<'changed' | 'exhausted' | 'error' | null>(null);
  const [attempt, setAttempt] = useState(0);
  const pages = useRef(1),
    generation = useRef(0),
    reader = useRef(read);
  const visible = useRef<string[]>([]);
  const activeKey = useRef(key),
    poll = useRef(polling);
  reader.current = read;
  poll.current = polling;
  if (activeKey.current !== key) {
    activeKey.current = key;
    pages.current = 1;
    visible.current = [];
  }
  useEffect(() => {
    const id = ++generation.current;
    const abort = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined,
      failures = 0;
    const current = () => generation.current === id && activeKey.current === key && !abort.signal.aborted;
    const started = Date.now();
    const load = async () => {
      if (!current()) return;
      setBusy(true);
      const request = new AbortController();
      const cancel = () => request.abort();
      abort.signal.addEventListener('abort', cancel, { once: true });
      const timeout = setTimeout(cancel, 15000);
      let again = false;
      try {
        const result = await readHotelResultWindow(
          reader.current,
          request.signal,
          pages.current,
          visible.current,
          identity,
          (count) => {
            if (current()) pages.current = count;
          },
        );
        if (!current() || request.signal.aborted) return;
        visible.current = identity ? result.data.map(identity) : [];
        setStored((previous) => ({
          key,
          page:
            identity && previous.key === key && previous.page
              ? { ...result, data: stableResultRows(previous.page.data, result.data, identity) }
              : result,
        }));
        setError(null);
        failures = 0;
        again = poll.current;
      } catch (e) {
        if (!current()) return;
        const changed =
          apiErrorCode(e) === 'HOTEL_RESULTS_CHANGED' || (e instanceof Error && e.message === 'HOTEL_RESULTS_CHANGED');
        // Preserve the last coherent snapshot during the bounded automatic refresh.
        const recovery = changed ? hotelRevisionRetry(++failures) : { error: 'error' as const, again: false };
        again = recovery.again;
        setError(recovery.error);
      } finally {
        clearTimeout(timeout);
        abort.signal.removeEventListener('abort', cancel);
        if (current()) {
          setBusy(false);
          if (request.signal.aborted) setError('error');
          if (again && Date.now() - started < 120000) timer = setTimeout(load, 1500);
          else if (again) setError('error');
        }
      }
    };
    void load();
    return () => {
      generation.current++;
      abort.abort();
      clearTimeout(timer);
    };
  }, [key, attempt, polling]);
  return {
    page,
    busy,
    error,
    reload: () => setAttempt((n) => n + 1),
    more: () => {
      if (page?.meta.next_cursor && !busy) {
        pages.current++;
        setAttempt((n) => n + 1);
      }
    },
  };
}
